import React, { useCallback, useLayoutEffect, useMemo, useState } from "react";
import { Alert, CircularProgress } from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { RoutePaths } from "../../../../enumStrings";
import {
  CmpProspectNoteReturn,
  CmpProspectStatusEnum,
  useCreateCmpProspectLeadCaptureMutation,
  useCreateCmpProspectNoteMutation,
  useGetCmpProspectDetailQuery,
  useGetCmpProspectLeadCapturesQuery,
  useGetCmpProspectNotesQuery,
  useGetCmpProspectStaffOptionsQuery,
  useUpdateCmpProspectMutation,
} from "../../../../graphql/types";
import { useGetUserFromUserIdInAuthState } from "../../../../hooks/useGetUserFromUserIdInAuth";
import { usePageHeaderOverride } from "../../../Layout/PageContainer";
import {
  cmpShortDateFormatter,
  cmpShortDateTimeFormatter,
} from "../../../common/dashboard/formatters";
import {
  ProspectForm,
  ProspectFormAction,
  ProspectFormSaveInput,
} from "../ProspectForm";
import {
  getProspectStaffDisplayName,
  mapProspectToFormValues,
  PROSPECT_ERROR_CONTEXT,
  ProspectSaveResult,
} from "../prospectFormModel";
import { ProspectConversionSummary } from "../ProspectConversionSummary";
import { ProspectResultDialog } from "../ProspectResultDialog";
import { prospectDesignFontFamily, prospectHeaderStyles } from "../prospectDesign";
import { InvoiceDesignScope } from "../../Invoicing/InvoiceDesignSystem";
import { useCmpOrganizationNoun } from "../../../../contexts/CmpOrganizationNounContext";
import {
  buildStaffErrorFallback,
  getUserFacingErrorText,
  UserFacingError,
} from "../../../../utils/userFacingError";

type UpdateProspectLocationState = {
  prospectId?: string;
  cmpProspectId?: string;
};

const formatRelativeUpdatedTime = (timestamp: number): string => {
  const elapsedDays = Math.floor(
    Math.max(0, Date.now() - timestamp) / (24 * 60 * 60 * 1000),
  );
  return elapsedDays === 0
    ? "today"
    : elapsedDays === 1
      ? "1 day ago"
      : elapsedDays + " days ago";
};

const ProspectHeaderStamp = ({
  timestamp,
  converted,
}: {
  timestamp?: number | null;
  converted: boolean;
}) => {
  if (
    !timestamp ||
    !Number.isFinite(timestamp) ||
    !Number.isFinite(new Date(timestamp).getTime())
  )
    return null;
  return (
    <div
      className="flex shrink-0 items-start gap-[10px] text-right text-[14px] leading-[normal]"
      style={{ fontFamily: '"DM Sans", sans-serif' }}
    >
      <div className="flex flex-col items-end gap-[2px]">
        <div className="font-semibold text-[#0A0A0A]">
          {converted
            ? "Converted to Member"
            : "Last Updated " + formatRelativeUpdatedTime(timestamp)}
        </div>
        <div className="text-[#727272]">
          {converted
            ? cmpShortDateFormatter.format(timestamp)
            : cmpShortDateTimeFormatter.format(timestamp)}
        </div>
      </div>
      <svg
        aria-hidden="true"
        className="mt-[2px]"
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#0A0A0A"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M12 6v6l4 2" />
      </svg>
    </div>
  );
};

const UpdateProspect = () => {
  const noun = useCmpOrganizationNoun();
  const { prospectId: prospectIdFromPath } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const setPageHeaderOverride = usePageHeaderOverride();
  const { user } = useGetUserFromUserIdInAuthState();
  const chamberId = user?.chamberInfo?.id || "";
  const currentStaffDisplayName = getProspectStaffDisplayName({ user });
  const locationState = location.state as UpdateProspectLocationState | null;
  const prospectId =
    prospectIdFromPath ||
    locationState?.prospectId ||
    locationState?.cmpProspectId ||
    new URLSearchParams(location.search).get("prospectId") ||
    "";
  const [savedCompanyName, setSavedCompanyName] = useState<string | null>(null);
  const [createdNotes, setCreatedNotes] = useState<CmpProspectNoteReturn[]>([]);

  const {
    data: detailData,
    loading: detailLoading,
    error: detailError,
  } = useGetCmpProspectDetailQuery({
    context: PROSPECT_ERROR_CONTEXT,
    variables: { input: { chamberId, prospectId } },
    skip: !chamberId || !prospectId,
    fetchPolicy: "cache-and-network",
  });
  const {
    data: leadCaptureData,
    loading: leadCapturesLoading,
    error: leadCapturesQueryError,
  } = useGetCmpProspectLeadCapturesQuery({
    context: PROSPECT_ERROR_CONTEXT,
    variables: { input: { chamberId } },
    skip: !chamberId,
    fetchPolicy: "cache-and-network",
  });
  const {
    data: staffData,
    loading: staffLoading,
    error: staffError,
  } = useGetCmpProspectStaffOptionsQuery({
    context: PROSPECT_ERROR_CONTEXT,
    variables: { input: { chamberId } },
    skip: !chamberId,
    fetchPolicy: "cache-and-network",
  });
  const {
    data: notesData,
    loading: notesLoading,
    error: notesError,
  } = useGetCmpProspectNotesQuery({
    context: PROSPECT_ERROR_CONTEXT,
    variables: { input: { chamberId, prospectId } },
    skip: !chamberId || !prospectId,
    fetchPolicy: "cache-and-network",
  });
  const [updateProspect] = useUpdateCmpProspectMutation({
    context: PROSPECT_ERROR_CONTEXT,
  });
  const [createLeadCapture] = useCreateCmpProspectLeadCaptureMutation({
    context: PROSPECT_ERROR_CONTEXT,
  });
  const [createNote] = useCreateCmpProspectNoteMutation({
    context: PROSPECT_ERROR_CONTEXT,
  });
  const queriedProspect = detailData?.getCmpProspectDetail;
  const prospect =
    queriedProspect?.id === prospectId &&
    queriedProspect.chamberId === chamberId
      ? queriedProspect
      : undefined;
  const converted = prospect?.status === CmpProspectStatusEnum.Converted;
  const headerActions = useMemo(
    () => (
      <ProspectHeaderStamp
        converted={converted}
        timestamp={
          converted
            ? prospect?.convertedAtUtcMilli
            : prospect?.updatedAtUtcMilli
        }
      />
    ),
    [converted, prospect?.convertedAtUtcMilli, prospect?.updatedAtUtcMilli],
  );
  const headerOverride = useMemo(
    () => ({
      ...prospectHeaderStyles,
      title:
        (converted ? "View " : "Update ") +
        (prospect?.companyName || "Prospect"),
      description: converted
        ? "This prospect has been converted to a member. The prospect record is kept for conversion tracking. To update their information, go to Manage Members."
        : "Use this section to update a prospect’s information. Click Save button to save all changes. To convert prospect to a member, click Convert to Member button.",
      actions: headerActions,
    }),
    [converted, headerActions, prospect?.companyName],
  );
  useLayoutEffect(() => {
    setPageHeaderOverride(headerOverride);
    return () => setPageHeaderOverride(null);
  }, [headerOverride, setPageHeaderOverride]);

  const notes = useMemo(() => {
    // The mutation response is the immediate readback while the notes index catches up.
    const byId = new Map<string, CmpProspectNoteReturn>();
    [...(notesData?.getCmpProspectNotes || []), ...createdNotes].forEach(
      (note) => {
        if (note.chamberId === chamberId && note.prospectId === prospectId)
          byId.set(note.id, note);
      },
    );
    return Array.from(byId.values()).sort(
      (left, right) => right.createdAtUtcMilli - left.createdAtUtcMilli,
    );
  }, [chamberId, createdNotes, notesData, prospectId]);

  const handleCreateLeadCapture = useCallback(
    async (label: string) => {
      if (!chamberId)
        throw new UserFacingError(`Your ${noun.singular} details are still loading. Wait a moment, then try again. If it keeps happening, refresh the page.`);
      const response = await createLeadCapture({
        variables: {
          input: {
            chamberId,
            label,
            isCmpProspectModuleTestData: process.env.NODE_ENV === "development",
          },
        },
      });
      const payload = response.data?.createCmpProspectLeadCapture;
      if (!payload?.leadCaptureSavedSuccessfully || !payload.leadCapture)
        throw new Error("The lead capture could not be saved.");
      return { id: payload.leadCapture.id, label: payload.leadCapture.label };
    },
    [chamberId, createLeadCapture, noun],
  );

  const handleSave = useCallback(
    async ({ data }: ProspectFormSaveInput) => {
      if (!chamberId || !prospectId)
        throw new UserFacingError("This prospect is still loading. Wait a moment, then try again.");
      const response = await updateProspect({
        variables: { input: { chamberId, prospectId, data } },
      });
      const payload = response.data?.updateCmpProspect;
      if (!payload?.prospectSavedSuccessfully || !payload.prospect)
        throw new Error("The prospect could not be updated.");
      return payload.prospect;
    },
    [chamberId, prospectId, updateProspect],
  );

  const handleAddNote = useCallback(
    async (comment: string) => {
      if (!chamberId || !prospectId)
        throw new UserFacingError("This prospect is still loading. Wait a moment, then try again.");
      const response = await createNote({
        variables: { input: { chamberId, prospectId, comment } },
      });
      const payload = response.data?.createCmpProspectNote;
      if (!payload?.noteSavedSuccessfully || !payload.note)
        throw new Error("The note could not be saved.");
      setCreatedNotes((current) => [
        payload.note,
        ...current.filter((note) => note.id !== payload.note.id),
      ]);
    },
    [chamberId, createNote, prospectId],
  );

  const handleSaved = useCallback(
    (savedProspect: ProspectSaveResult, action: ProspectFormAction) => {
      if (action === "convert") {
        navigate(
          RoutePaths.chamberAdmin_members_create +
            "?prospectId=" +
            encodeURIComponent(savedProspect.id),
        );
        return;
      }
      setSavedCompanyName(savedProspect.companyName);
    },
    [navigate],
  );

  if (!prospectId)
    return (
      <InvoiceDesignScope fontFamily={prospectDesignFontFamily}>
        <Alert severity="error">
          No prospect was selected. Return to Manage Prospects and choose a
          prospect to update.
        </Alert>
      </InvoiceDesignScope>
    );
  if ((!chamberId || detailLoading) && !prospect)
    return (
      <InvoiceDesignScope fontFamily={prospectDesignFontFamily}>
        <div className="flex min-h-[320px] items-center justify-center gap-3 text-[14px] font-semibold text-[#727272]">
          <CircularProgress size={24} />
          Loading prospect...
        </div>
      </InvoiceDesignScope>
    );
  if (detailError || !prospect)
    return (
      <InvoiceDesignScope fontFamily={prospectDesignFontFamily}>
        <Alert severity="error">
          {getUserFacingErrorText(detailError, buildStaffErrorFallback("load this prospect"))}
        </Alert>
      </InvoiceDesignScope>
    );

  const viewMemberPath = prospect.convertedMemberId
    ? RoutePaths.chamberAdmin_members_updateLegacy.replace(
        ":memberId",
        encodeURIComponent(prospect.convertedMemberId),
      )
    : undefined;

  return (
    <>
      <ProspectForm
        key={prospect.id}
        mode={converted ? "converted" : "update"}
        initialValues={mapProspectToFormValues(prospect)}
        leadCaptures={leadCaptureData?.getCmpProspectLeadCaptures || []}
        leadCapturesLoading={leadCapturesLoading}
        leadCapturesError={
          leadCapturesQueryError
            ? "We could not load the Lead Capture list. Refresh the page to try again, or select Add New Lead Capture."
            : undefined
        }
        staffOptions={staffData?.getCmpProspectStaffOptions || []}
        staffOptionsLoading={staffLoading}
        staffOptionsError={
          staffError
            ? "We could not load the staff list for Created By. Refresh the page to try again."
            : undefined
        }
        currentStaffDisplayName={currentStaffDisplayName}
        notes={notes}
        notesLoading={notesLoading}
        notesError={
          notesError
            ? "We could not load the notes history for this prospect. Refresh the page to try again."
            : undefined
        }
        onCreateLeadCapture={handleCreateLeadCapture}
        onSave={handleSave}
        onSaved={handleSaved}
        onAddNote={handleAddNote}
        viewMemberPath={viewMemberPath}
        conversionSummary={
          converted ? (
            <ProspectConversionSummary prospect={prospect} />
          ) : undefined
        }
      />
      <ProspectResultDialog
        open={savedCompanyName !== null}
        kind="saved"
        companyName={savedCompanyName || ""}
        onClose={() => {
          setSavedCompanyName(null);
          navigate(RoutePaths.chamberAdmin_prospects_manage);
        }}
      />
    </>
  );
};

export default UpdateProspect;
